import {
  User,
  Plane,
  Briefcase,
  CreditCard,
  FileText,
  ChevronDown,
  CalendarClock,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type {
  FlightDatetimeChangedMetadata,
  FlightOrder,
  HistoryEventType,
  Order,
  SoloOrder,
} from "../../types/order.types";
import {
  addOrderComment,
  comfirmPaymentStatus,
  downloadOrderTicket,
  getOrderById,
} from "../../api/orders";
import { getUsers } from "@/features/users/api/users";
import type { User as Manager } from "@/features/users/types/users.types";
import { cn, encodeOrderId } from "@/shared/utils";
import { publicEnv } from "@/shared/utils/env";
import { Badge } from "@/shared/components/ui/badge";
import { toast } from "sonner";
import { PaymentConfirmModal } from "./PaymentConfirmModal";
import { getStatusConfig } from "../../constants/orders.constants";
import airAsia from "../../../../shared/images/airlines/air_asia.png";
import airAstana from "../../../../shared/images/airlines/air_astana.png";
import airCairo from "../../../../shared/images/airlines/air_cairo.png";
import defaultAirline from "../../../../shared/images/airlines/default.png";
import flyArystan from "../../../../shared/images/airlines/fly_arystan.png";
import pegasus from "../../../../shared/images/airlines/pegasus.png";
import redSea from "../../../../shared/images/airlines/red_sea.png";
import scat from "../../../../shared/images/airlines/scat.png";
import sunday from "../../../../shared/images/airlines/sunday.png";
import turkish from "../../../../shared/images/airlines/turkish_airlines.png";
import vietjet from "../../../../shared/images/airlines/vietjet.png";

export const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}мин`;
  if (m === 0) return `${h}ч`;
  return `${h}ч ${m}мин`;
};

const airlineIcons: Record<string, string> = {
  "SCAT Airlines": scat,
  "Air Astana": airAstana,
  FlyArystan: flyArystan,
  "VietJet Air": vietjet,
  "Red Sea Airlines": redSea,
  "Sunday Airlines": sunday,
  "Air Cairo": airCairo,
  "Turkish Airlines": turkish,
  "Pegasus Airlines": pegasus,
  "AirAsia X Berhad": airAsia,
};

const getAirlineIcon = (airline?: string) => {
  if (airline && airlineIcons[airline]) return airlineIcons[airline];
  return defaultAirline;
};
export default function OrderDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // получаем id из URL [web:168]
  const [order, setOrder] = useState<FlightOrder | null>(null);
  const [authors, setAuthors] = useState<Record<number, Manager>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPassengers, setExpandedPassengers] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmPaymentModalOpen, setIsConfirmPaymentModalOpen] =
    useState(false);

  const handleAddComment = async () => {
    if (!id || !comment.trim()) return;
    setIsSubmitting(true);
    try {
      await addOrderComment(id, comment.trim());
      setComment("");
      toast.success("Комментарий добавлен");
      run();
      // при необходимости обновить данные заказа: refetch()
    } catch (error) {
      toast.error("Не удалось добавить комментарий");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePassenger = (id: string) => {
    setExpandedPassengers(
      (prev) =>
        prev.includes(id)
          ? prev.filter((pid) => pid !== id) // закрыть
          : [...prev, id] // открыть
    );
  };

  const handleDownloadTicket = async () => {
    if (!id) return;
    setIsDownloading(true);
    try {
      await downloadOrderTicket(id);
    } catch (error) {
      toast.error("Не удалось скачать билет");
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };
  const run = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await getOrderById(id);
      console.log(data.data);

      setOrder(data.data);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Ошибка";
      toast.error(message);
      console.error(e);
      setError("Не удалось загрузить заказ");
    } finally {
      setIsLoading(false);
    }
  };
  const handleChangePaymentType = async () => {
    try {
      if (id) {
        await comfirmPaymentStatus(id);
        toast.success("Оплата подтверждена");
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Ошибка";
      toast.error(message);
      console.error(e);
    }
  };
  useEffect(() => {
    run();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    getUsers()
      .then((managers) => {
        if (cancelled) return;
        const map: Record<number, Manager> = {};
        for (const manager of managers) map[manager.id] = manager;
        setAuthors(map);
      })
      .catch((e) => {
        console.error("Не удалось загрузить менеджеров", e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getHistoryIcon = (type: HistoryEventType) => {
    switch (type) {
      case "created":
        return { Icon: User, bg: "#E7F0FF", color: "#003399" };
      case "payment_received":
        return { Icon: CreditCard, bg: "#E7F0FF", color: "#003399" };
      case "ticket_sent":
        return { Icon: FileText, bg: "#E7F0FF", color: "#003399" };
      case "comment":
      default:
        return { Icon: User, bg: "#EEF2FF", color: "#4F46E5" };
    }
  };

  const PASSENGER_CATEGORY_LABEL: Record<string, string> = {
    adult: "взр",
    child: "реб",
    infant: "млад",
  };
  const parseDate = (value: string): Date => {
    if (/^\d{2}-\d{2}-\d{4}/.test(value)) {
      const [datePart = "", timePart = "00:00"] = value.split(" ");
      const [day = "01", month = "01", year = "2000"] = datePart.split("-");
      return new Date(`${year}-${month}-${day}T${timePart}:00`);
    }
    return new Date(value);
  };

  const formatTime = (value: string) =>
    parseDate(value).toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDateShort = (value: string) =>
    parseDate(value).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "short",
      weekday: "short",
    });

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU"); // "12.03.2025"

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      // Flight datetimes are wall-clock times stored with a misleading "Z"
      // suffix; render them as-is (UTC) instead of shifting to local time.
      timeZone: "UTC",
    }); // "16.06.2026, 11:11"

  const formatPrice = (value: number) =>
    new Intl.NumberFormat("ru-RU").format(value);

  type OrderStatus = "pending" | "processed" | "cancelled";

  const mapOrderStatusToBadges = (status: OrderStatus) => {
    const badges: {
      label: string;
      variant: "primary" | "success" | "info" | "warning" | "danger" | "muted";
    }[] = [];

    switch (status) {
      case "processed":
        badges.push(
          { label: "Оформлен", variant: "success" },
          { label: "Оплачен", variant: "primary" },
          { label: "Невозвратный", variant: "danger" } // когда появится флаг, сделаешь условным
        );
        break;

      case "pending":
        badges.push(
          { label: "Оформлен", variant: "success" },
          { label: "Ожидает", variant: "info" }
        );
        break;

      /* case "waiting":
        badges.push({ label: "Ожидает", variant: "warning" });
        break; */

      case "cancelled":
        badges.push({ label: "Отменён", variant: "danger" });
        break;

      default:
        badges.push({ label: "Ожидает", variant: "muted" });
    }

    return badges;
  };

  const handleCopyPaymentLink = () => {
    if (!order) return;
    const encodedId = encodeOrderId(order.id);
    const link = `${publicEnv.BUN_PUBLIC_PAYMENT_URL}/order/${encodedId}`;
    navigator.clipboard.writeText(link);
    toast.success("Ссылка скопирована");
  };

  const cabinClassMap: Record<string, string> = {
    economy: "Эконом",
    business: "Бизнес",
    first: "Первый класс",
    premium: "Премиум",
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-grey)] p-6 font-sans">
      <div className="max-w-[1400px] mx-auto">
        {/* Хлебные крошки */}
        <div
          className="mb-6 text-[14px] text-[var(--color-text-secondary)] cursor-pointer hover:text-[#003399] transition-colors"
          onClick={() => navigate(`/`)}
        >
          Главная / Заказы /{" "}
          <span className="text-[#003399] font-medium">{order?.id}</span>
        </div>

        {/* ЖЕСТКАЯ СЕТКА БЕЗ АДАПТИВА (Только Десктоп) */}
        <div className="grid grid-cols-[minmax(0,1fr)_360px] gap-2 items-start">
          {/* ==========================================
              LEFT (Детали заказа + История)
          ========================================== */}
          <div className="space-y-2">
            {/* БЛОК 1: ДЕТАЛИ ЗАКАЗА */}
            <div className="rounded-xl bg-white border shadow-[0_1px_2px_rgba(16,24,40,0.06)] overflow-hidden">
              <div className="px-6 py-5 border-b">
                <h1 className="text-[18px] font-semibold text-[var(--color-text-primary)]">
                  Детали заказа {order?.id}
                </h1>
              </div>

              <div className="p-6 space-y-8">
                {/* Секция: Клиент */}
                <div>
                  <div className="mb-4">
                    <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
                      Клиент
                    </h3>
                    <p className="text-[12px] text-[var(--color-text-secondary)] mt-1">
                      Данные клиента
                    </p>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-white cursor-pointer border-b">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-[#E7F0FF] flex items-center justify-center text-[#003399]">
                          <User className="w-4 h-4" />
                        </div>
                        <span className="font-semibold text-[14px] text-[var(--color-text-primary)]">
                          {`${order?.customer.name} ${order?.customer.surname}`}
                        </span>
                      </div>
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="p-4 bg-gray-50/50 space-y-3">
                      <div className="bg-white border  rounded-md px-4 py-2.5 text-[13px] text-gray-500">
                        Имя: {order?.customer.name}
                      </div>
                      <div className="bg-white border rounded-md px-4 py-2.5 text-[13px] text-gray-500">
                        Фамилия: {order?.customer.surname}
                      </div>
                      <div className="bg-white border rounded-md px-4 py-2.5 text-[13px] text-gray-500">
                        Телефон: {order?.customer.phone}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Секция: Пассажиры */}
                <div>
                  <div className="mb-4">
                    <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
                      Информация о пассажирах
                    </h3>
                    <p className="text-[12px] text-[var(--color-text-secondary)] mt-1">
                      Данные пассажиров
                    </p>
                  </div>

                  <div className="space-y-3">
                    {order?.passengers.map((passenger) => {
                      const isExpanded = expandedPassengers.includes(
                        passenger.id
                      );
                      const ticketType = "Взрослый"; // или бери из данных

                      return (
                        <div
                          key={passenger.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div
                            className="flex items-center justify-between p-4 bg-white cursor-pointer"
                            onClick={() => togglePassenger(passenger.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded bg-[#E7F0FF] flex items-center justify-center text-[#003399]">
                                <User className="w-4 h-4" />
                              </div>
                              <span className="font-semibold text-[14px] text-[var(--color-text-primary)]">
                                {passenger.name} (
                                {PASSENGER_CATEGORY_LABEL[
                                  passenger.passenger_category
                                ] ?? "—"}
                                )
                              </span>
                            </div>
                            <ChevronDown
                              className={cn(
                                "w-5 h-5 text-gray-400 transition-transform",
                                isExpanded && "rotate-180"
                              )}
                            />
                          </div>

                          {isExpanded && (
                            <div className="p-4 bg-gray-50/50 space-y-4">
                              <div>
                                <div className="text-[12px] text-[var(--color-text-secondary)] mb-1">
                                  Дата рождения:
                                </div>
                                <div className="bg-white border rounded-md px-4 py-2.5 text-[13px] text-gray-500">
                                  {new Date(
                                    passenger.dateOfBirth
                                  ).toLocaleDateString("ru-RU")}
                                </div>
                              </div>

                              <div>
                                <div className="text-[12px] text-[var(--color-text-secondary)] mb-1">
                                  Документ:
                                </div>
                                <div className="bg-white border rounded-md px-4 py-2.5 text-[13px] text-gray-500">
                                  {passenger.passportNumber}
                                </div>
                              </div>

                              <div>
                                <div className="text-[12px] text-[var(--color-text-secondary)] mb-1">
                                  Тип билета:
                                </div>
                                <div className="bg-white border rounded-md px-4 py-2.5 text-[13px] text-gray-500">
                                  {ticketType}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* БЛОК 2: ИСТОРИЯ ЗАКАЗА */}
            <div className="rounded-xl bg-white border shadow-[0_1px_2px_rgba(16,24,40,0.06)] overflow-hidden">
              <div className="p-6 border-b">
                <h1 className="text-[18px] font-semibold text-[var(--color-text-primary)]">
                  История заказа
                </h1>
                <p className="text-[12px] text-[var(--color-text-secondary)] mt-1">
                  События и комментарии по заказу
                </p>
              </div>

              <div className="p-6">
                <div className="relative border-l border-gray-200 ml-4 py-2 space-y-6 mb-8">
                  {order?.history.map((item) => {
                    const author =
                      item.authorId != null
                        ? authors[item.authorId]
                        : undefined;
                    const datetimeChange =
                      item.eventType === "flight_datetime_changed"
                        ? (item.metadata as
                            | FlightDatetimeChangedMetadata
                            | null
                            | undefined)
                        : undefined;
                    return (
                      <div key={item.id} className="relative flex gap-2">
                        <span className="w-8 h-8 rounded bg-[#E7F0FF] flex items-center justify-center text-[#003399]">
                          {item.eventType === "flight_datetime_changed" ? (
                            <CalendarClock className="w-4 h-4" />
                          ) : (
                            <FileText className="w-4 h-4" />
                          )}
                        </span>
                        <div>
                          <h4 className="text-[14px] font-semibold text-[var(--color-text-primary)]">
                            {item.message}
                          </h4>
                          <p className="text-[12px] text-[var(--color-text-secondary)] mt-1">
                            {new Date(item.createdAt).toLocaleString("ru-RU")}
                          </p>
                          {author && (
                            <p className="text-[12px] text-[var(--color-text-secondary)] mt-1">
                              {author.name} {author.surname}
                            </p>
                          )}
                          {datetimeChange && (
                            <div className="mt-2 rounded-lg border bg-gray-50 p-3 space-y-2 text-[12px]">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="w-14 text-[var(--color-text-secondary)]">
                                  Вылет:
                                </span>
                                <span className="text-[#B42318] line-through">
                                  {formatDateTime(
                                    datetimeChange.oldDepartureAt
                                  )}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="font-semibold text-[#027A48]">
                                  {formatDateTime(
                                    datetimeChange.newDepartureAt
                                  )}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="w-14 text-[var(--color-text-secondary)]">
                                  Прилёт:
                                </span>
                                <span className="text-[#B42318] line-through">
                                  {formatDateTime(datetimeChange.oldArrivalAt)}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="font-semibold text-[#027A48]">
                                  {formatDateTime(datetimeChange.newArrivalAt)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {order && order.history.length === 0 && (
                    <p className="text-[13px] text-[var(--color-text-secondary)] ml-10">
                      История по заказу пока отсутствует
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="Добавить комментарий"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                    disabled={isSubmitting}
                    className="flex-1 border bg-gray-50 rounded-lg px-4 text-[13px] outline-none h-11 disabled:opacity-60"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={isSubmitting || !comment.trim()}
                    className="bg-[#003399] hover:bg-blue-800 text-white px-6 h-11 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Отправка..." : "Добавить комментарий"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ==========================================
              RIGHT (Информация о рейсе)
          ========================================== */}
          {order && (
            <aside className="rounded-xl bg-white border shadow-[0_1px_2px_rgba(16,24,40,0.06)] overflow-hidden space-y-3 sticky top-6">
              <div className="px-6 py-5 border-b">
                <h2 className="text-[18px] font-semibold text-[var(--color-text-primary)]">
                  Информация о рейсе
                </h2>
              </div>

              <div className="px-6 pb-6 pt-2">
                {/* Бейджи статуса */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <Badge className={getStatusConfig(order.status).className}>
                    {getStatusConfig(order.status).label}
                  </Badge>
                </div>

                {/* Номер заявки / Оператор */}
                {(order.providerNumber || order.operator) && (
                  <div className="mb-6 text-[13px] text-[var(--color-text-secondary)] space-y-1">
                    {order.providerNumber && (
                      <p>Номер заявки - {order.providerNumber}</p>
                    )}
                    {order.operator && <p>Оператор: {order.operator}</p>}
                  </div>
                )}

                {/* Направление */}
                <div className="mb-6">
                  {order.segments.map((segment, index) => (
                    <div
                      key={segment.id}
                      className={index > 0 ? "mt-8 pt-8 border-t" : ""}
                    >
                      <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
                        {segment.from.city} → {segment.to.city}
                      </h3>
                      <p className="text-[12px] text-[var(--color-text-secondary)] mt-0.5 mb-5">
                        Прямой рейс
                      </p>

                      {/* Авиакомпания / номер рейса */}
                      <div className="flex items-center justify-between text-[13px] mb-5">
                        <div className="flex items-center gap-2 font-semibold text-[var(--color-text-primary)]">
                          <img
                            src={getAirlineIcon(segment.airline)}
                            alt={segment.airline}
                            className="w-4 h-4"
                          />
                          {segment.airline}
                        </div>
                        <span className="text-[var(--color-text-secondary)]">
                          Рейс: {segment.flightNumber}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[13px] mb-5">
                        <span className="text-[var(--color-text-secondary)]">
                          {cabinClassMap[segment.class] ?? segment.class} |{" "}
                          {segment.duration &&
                            formatDuration(Number(segment.duration))}
                        </span>
                      </div>

                      {/* Вылет / прилёт */}
                      <div className="relative border-l-2 border-dashed border-gray-200 ml-[9px] pl-5 space-y-6 my-5 mb-4">
                        {/* Вылет */}
                        <div className="relative">
                          <div className="flex flex-start items-start gap-4">
                            <div>
                              <div className="font-semibold text-[14px] text-[var(--color-text-primary)]">
                                {formatTime(segment.departureAt)}
                              </div>
                              <div className="text-[12px] text-[var(--color-text-secondary)] mt-0.5">
                                {formatDateShort(segment.departureAt)}
                              </div>
                            </div>
                            <div>
                              <div className="font-semibold text-[14px] text-[var(--color-text-primary)]">
                                {segment.from.city}
                              </div>
                              <div className="text-[12px] text-[var(--color-text-secondary)] mt-0.5">
                                {segment.from.airport},{" "}
                                {segment.from.airportCode}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Прилёт */}
                        <div className="relative">
                          <div className="flex flex-start items-start gap-4">
                            <div>
                              <div className="font-semibold text-[14px] text-[var(--color-text-primary)]">
                                {formatTime(segment.arrivalAt)}
                              </div>
                              <div className="text-[12px] text-[var(--color-text-secondary)] mt-0.5">
                                {formatDateShort(segment.arrivalAt)}
                              </div>
                            </div>
                            <div>
                              <div className="font-semibold text-[14px] text-[var(--color-text-primary)]">
                                {segment.to.city}
                              </div>
                              <div className="text-[12px] text-[var(--color-text-secondary)] mt-0.5">
                                {segment.to.airport}, {segment.to.airportCode}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Багаж */}
                      <div className="flex items-center gap-3 text-[13px] text-[var(--color-text-secondary)] bg-gray-50 p-3 rounded-lg border">
                        <Briefcase className="w-4 h-4" />
                        <span className="font-medium">
                          Ручная кладь: {segment.baggage.baggageCarryOnKg} кг
                          {" · "}
                          Багаж: {segment.baggage.baggageCheckedKg} кг
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Цена и оплата */}
                <div className="pt-5 border-t space-y-2 text-[13px] mb-6">
                  <div className="flex justify-between">
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      Цена:
                    </span>
                    <span className="text-[var(--color-text-primary)]">
                      {formatPrice(order.totalPrice)} ₸ ×{" "}
                      {order.passengers.length} пассажир
                      {order.passengers.length > 1 ? "а" : ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      Способ оплаты:
                    </span>
                    <span className="text-[var(--color-text-secondary)]">
                      Карта
                      {/* когда появится поле paymentMethod, подставишь его */}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      Дата оплаты:
                    </span>
                    <span className="text-[var(--color-text-secondary)]">
                      {formatDate(order.createdAt)}
                      {/* или отдельное поле paymentDate, если будет */}
                    </span>
                  </div>
                </div>

                {/* Кнопки */}
                <div className="space-y-2">
                  <button
                    onClick={handleDownloadTicket}
                    className="w-full bg-[#003399] hover:bg-blue-800 text-white font-semibold h-10 rounded-lg transition-colors text-[13px]"
                  >
                    Электронный билет (PDF)
                  </button>
                  {order?.status !== "paid" && (
                    <>
                      <button
                        onClick={() => setIsConfirmPaymentModalOpen(true)}
                        className="w-full text-[#003399] hover:text-blue-800 font-semibold h-10 rounded-lg transition-colors text-[13px]"
                      >
                        Сменить статус на оплачено
                      </button>
                      <button
                        onClick={handleCopyPaymentLink}
                        className="w-full bg-[#003399] hover:bg-blue-800 text-white font-semibold h-10 rounded-lg transition-colors text-[13px]"
                      >
                        Копировать ссылку для оплаты
                      </button>
                    </>
                  )}
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>
      <PaymentConfirmModal
        open={isConfirmPaymentModalOpen}
        onOpenChange={setIsConfirmPaymentModalOpen}
        id={id}
      />
    </div>
  );
}
